import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';

import type { PaginationFilter } from '@/types/PaginationFilter';

import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import { SearchFollowSuggestions } from '@/components/SearchFollowSuggestions/SearchFollowSuggestions';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { useAppSelector } from '@/redux/hooks';
import {
    getTotalSavedSearches,
    isSavedSearchesLoading,
    savedSearchSearchesSelector,
} from '@/redux/modules/savedSearch';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { ManageSavedSearchFilter } from './components/ManageSavedSearchFilter';
import { ManageSavedSearchGrid } from './components/ManageSavedSearchGrid';

type Props = {
    filters: PaginationFilter;
    onSavedSearchRecentFilterChange: (filters: PaginationFilter) => void;
};

/**
 * savedsearch-api filters by keyword before counting, so `totalSavedSearchesCount` is the size of
 * the filtered set: it drives the pager, and a keyword that matches nothing gets its own message
 * rather than the "not following any searches" onboarding copy.
 */
export const ManageSavedSearches = (props: Props) => {
    const { filters, onSavedSearchRecentFilterChange } = props;
    const isMobile = useIsNarrowerThan(769);
    const formatMessage = useAppFormatMessage();
    const isLoading = useAppSelector(isSavedSearchesLoading);
    const searches = useAppSelector(savedSearchSearchesSelector);
    const totalSavedSearchesCount = useAppSelector(getTotalSavedSearches);

    const keyword = filters.keyword?.trim() ?? '';
    const hasKeyword = Boolean(keyword);
    const isListEmpty = !isLoading && totalSavedSearchesCount === 0;
    const showPager = totalSavedSearchesCount > Number(filters.pageSize);

    const preparedSavedSearchFilter = (
        <ManageSavedSearchFilter
            filters={filters}
            onFilterChange={onSavedSearchRecentFilterChange}
        />
    );

    const grid = (
        <ManageSavedSearchGrid
            className="max-[560px]:mt-md mt-lg w-full max-w-full flex-[1_0_auto]"
            preparedSavedSearchFilter={preparedSavedSearchFilter}
            searches={searches}
            totalCount={totalSavedSearchesCount}
        />
    );

    return (
        <>
            {!isMobile && preparedSavedSearchFilter}
            {showPager ? (
                // The pager writes `page` to the URL, which is where ForYouPage reads it from.
                <Pagination
                    onChange={onSavedSearchRecentFilterChange}
                    paginationFilter={{ ...filters, totalRecords: totalSavedSearchesCount }}
                    shouldModifyQueryParams
                >
                    {grid}
                </Pagination>
            ) : (
                grid
            )}

            {isListEmpty && hasKeyword && (
                <BodyPrimary className="my-lg text-center">
                    {formatMessage(['search_alerts', 'noMatchingSearches'], { values: { keyword } })}
                </BodyPrimary>
            )}
            {isListEmpty && !hasKeyword && (
                <BodyPrimary className="my-lg text-center">
                    {formatMessage(['search_alerts', 'no_followed'])}
                </BodyPrimary>
            )}
            {isListEmpty && !hasKeyword && (
                <SearchFollowSuggestions
                    className="w-full [&>div[class*='altHeader']]:mx-0 [&>div[class*='altHeader']]:mt-0 [&>div[class*='altHeader']]:mb-[20px] [&>div[class*='side-scroller']]:w-full"
                    useAltHeader
                />
            )}
        </>
    );
};
